import React from "react"
import { Box, FormControl, Grid, MenuItem, Switch, TextField, Typography } from "@mui/material"
import { StyledSectionCard } from "shared/styled/General"
import { RECURRING_REMINDER_OPTIONS, REMINDER_OPTIONS } from "../constants/examinationSettings"
import { AutomaticReminderSectionProps } from "../interface/examinationSettings"

export const AutomaticRemindersSection: React.FC<AutomaticReminderSectionProps> = ({
    t,
    touched,
    errors,
    values,
    setFieldValue,
    reminderOptionName,
    recurringReminderName,
}) => {
    return (
        <StyledSectionCard>
            <Grid container gap={2}>
                <Grid item md={12} lg={6} xs={12}>
                    <Typography mb={1} fontWeight={600} variant="bodyXlarge">
                        {t(`automatic_reminder`, "Automatic reminders")}
                    </Typography>
                    <Typography color="textSecondary" variant="bodyMedium">
                        {t(
                            `automatic_reminder_text`,
                            "To ensure that participants do not forget about the training, you can configure reminders that are sent out before and after the last date of validity of the training."
                        )}
                    </Typography>
                </Grid>
                <Grid item md={12} display="flex" flexDirection="column" gap={2}>
                    <Grid container display="flex" alignItems="center" spacing={3} justifyContent="space-between">
                        <Grid item md={6}>
                            <Typography mb={1} variant="bodyLarge" fontWeight="600">
                                {t(`first_reminder`, "First reminder")}
                            </Typography>
                            <Typography color="textSecondary" variant="bodyMedium">
                                {t(
                                    `first_reminder_text`,
                                    "A reminder that is sent before the validity period of the training expires or the last date for a passed exam"
                                )}
                            </Typography>
                        </Grid>
                        <Grid item md={6} lg={4} xl={3} xs={12}>
                            <Typography mb={1} variant="bodyMedium">
                                {t(`first_reminder`, "First reminder")}
                            </Typography>
                            <Box display="flex" gap={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        select
                                        helperText={touched.firstReminderInDays ? errors.firstReminderInDays : ""}
                                        error={touched.firstReminderInDays && errors.firstReminderInDays ? true : false}
                                        disabled={values.sendFirstReminder ? false : true}
                                        id="firstReminderInDays"
                                        value={values.firstReminderInDays}
                                        onChange={(e) => {
                                            setFieldValue("firstReminderInDays", e.target.value)
                                        }}>
                                        {REMINDER_OPTIONS.map((item: any, index: number) => {
                                            return (
                                                <MenuItem value={item.value}>
                                                    {t(reminderOptionName(item.labelId))}
                                                </MenuItem>
                                            )
                                        })}
                                    </TextField>
                                </FormControl>
                                <Grid item display="flex" alignItems="center">
                                    <Switch
                                        onChange={(e) => {
                                            if (e.target.checked === true) {
                                                setFieldValue("sendFirstReminder", true)
                                            } else {
                                                setFieldValue("sendFirstReminder", false)
                                                setFieldValue("firstReminderInDays", null)
                                            }
                                        }}
                                        id="sendFirstReminder"
                                        checked={values.sendFirstReminder}
                                    />
                                    <Typography variant="bodyMedium">{t(`button.on`, "On")}</Typography>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} display="flex" alignItems="center" justifyContent="space-between">
                        <Grid item md={6}>
                            <Typography mb={1} fontWeight={600} variant="bodyLarge">
                                {t(`recurring_reminder`, "Recurring reminders")}
                            </Typography>
                            <Typography color="textSecondary" variant="bodyMedium">
                                {t(
                                    `recurring_reminder_text`,
                                    "Then continue to remind the participant until the training is completed"
                                )}
                            </Typography>
                        </Grid>
                        <Grid item md={6} lg={4} xl={3} xs={12}>
                            <Typography mb={1} variant="bodyMedium">
                                {t(`recurring_reminder`, "Recurring reminders")}
                            </Typography>
                            <Box display="flex" gap={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        select
                                        helperText={touched.recurringRemindersInDays ? errors.recurringRemindersInDays : ""}
                                        error={
                                            touched.recurringRemindersInDays && Boolean(errors.recurringRemindersInDays)
                                        }
                                        disabled={values.sendRecurringReminders ? false : true}
                                        id="recurringRemindersInDays"
                                        value={values.recurringRemindersInDays}
                                        onChange={(e) => {
                                            setFieldValue("recurringRemindersInDays", e.target.value)
                                        }}>
                                        {RECURRING_REMINDER_OPTIONS.map((item: any, index: number) => {
                                            return (
                                                <MenuItem key={index} value={item.value}>
                                                    {t(recurringReminderName(item.labelId))}
                                                </MenuItem>
                                            )
                                        })}
                                    </TextField>
                                </FormControl>
                                <Grid item display="flex" alignItems="center">
                                    <Switch
                                        onChange={(e) => {
                                            if (e.target.checked === true) {
                                                setFieldValue("sendRecurringReminders", true)
                                            } else {
                                                setFieldValue("sendRecurringReminders", false)
                                                setFieldValue("recurringRemindersInDays", null)
                                            }
                                        }}
                                        id="sendRecurringReminders"
                                        checked={values.sendRecurringReminders}
                                    />
                                    <Typography variant="bodyMedium">{t(`button.on`)}</Typography>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </StyledSectionCard>
    )
}